import React, { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import {
  SparklesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useTestCaseReview, useRunTestCaseReview } from "../../../useQuery/useTestCaseReview";

/**
 * Review of the test cases generated for one collection.
 *
 * Two layers, kept visually distinct because they carry different authority. Everything under
 * "measured" is counted from the stored test cases and the endpoint's own resolved contract. The
 * AI section interprets those figures - it never produces them.
 */

/**
 * Measurements held back for now.
 *
 * Each of these is accurate but reads as a bad number without the explanation that goes with it -
 * field targeting is capped by how much the contract actually specifies, payload depth is a
 * consequence of the generation prompt, and review progress is zero because nobody has started.
 * They are computed and returned by the API either way; flip this to surface them.
 */
const SHOW_ADVANCED_METRICS = false;

const SEVERITY = {
  high: { chip: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500" },
  medium: { chip: "bg-amber-50 text-amber-800 border-amber-200", bar: "bg-amber-500" },
  low: { chip: "bg-blue-50 text-blue-700 border-blue-200", bar: "bg-blue-400" },
};

const KIND_LABEL = {
  coverage: "Coverage",
  duplication: "Duplication",
  hygiene: "Data hygiene",
  process: "Process",
};

const TYPE_COLOURS = [
  "bg-blue-500", "bg-teal-500", "bg-amber-500", "bg-violet-500",
  "bg-rose-500", "bg-emerald-500", "bg-orange-400", "bg-slate-400",
];

const Tile = ({ label, value, sub, tone = "default" }) => {
  const tones = {
    default: "text-gray-900",
    good: "text-green-700",
    warn: "text-amber-700",
    bad: "text-red-700",
  };
  return (
    <div className="flex flex-col gap-0.5 px-4 py-3 bg-white border border-gray-200 min-w-[118px]">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-apple">{label}</span>
      <span className={`text-2xl font-semibold tabular-nums leading-none ${tones[tone]}`}>{value}</span>
      {sub && <span className="text-[11px] text-gray-500 font-apple">{sub}</span>}
    </div>
  );
};

const Section = ({ title, hint, children }) => (
  <div className="border border-gray-200 bg-white">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
      <span className="text-[13px] font-semibold text-gray-800">{title}</span>
      {hint && <span className="text-[11px] text-gray-400 truncate hidden sm:inline">{hint}</span>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const DistributionBar = ({ data, total, palette }) => (
  <div className="flex flex-col gap-2">
    <div className="flex h-2.5 w-full overflow-hidden rounded-sm bg-gray-100">
      {Object.entries(data).map(([key, count]) => (
        <Tooltip key={key} title={`${key}: ${count}`} arrow>
          <div className={palette(key)} style={{ width: `${total ? (count / total) * 100 : 0}%` }} />
        </Tooltip>
      ))}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {Object.entries(data).map(([key, count]) => (
        <span key={key} className="flex items-center gap-1.5 text-[11px] text-gray-600 font-apple">
          <span className={`inline-block h-2 w-2 rounded-sm ${palette(key)}`} />
          {key}
          <span className="tabular-nums text-gray-400">
            {count} · {total ? Math.round((count / total) * 100) : 0}%
          </span>
        </span>
      ))}
    </div>
  </div>
);

const Code = ({ children }) => (
  <span className="font-mono text-[11px] bg-gray-100 text-gray-700 px-1 py-0.5 rounded">{children}</span>
);

/** One endpoint measured against its own contract. Collapsed by default - this is detail, not headline. */
const EndpointCoverage = ({ endpoint }) => {
  const [open, setOpen] = useState(false);
  const c = endpoint.coverage || {};
  const sc = c.statusCodes || {};
  const sf = c.schemaFields || {};
  const pd = c.payloadDepth || {};
  const sq = c.scripts || {};
  const hd = c.headers || {};

  const shallow = sf.propertyCount > 0 && pd.median <= Math.max(3, sf.propertyCount * 0.15);

  return (
    <div className="border border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 text-left cursor-pointer"
      >
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
        />
        <span className="font-mono text-[11px] text-gray-500">{endpoint.method}</span>
        <span className="text-[13px] font-medium text-gray-900">{endpoint.path}</span>
        <span className="ml-auto flex items-center gap-2 flex-wrap text-[11px] tabular-nums">
          <span className="text-gray-500">{endpoint.totalTestCases} cases</span>
          <span
            className={`px-1.5 py-0.5 border ${
              sc.neverTested?.length
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-green-50 text-green-700 border-green-200"
            }`}
          >
            {sc.declaredCovered}/{sc.declaredTotal} codes
          </span>
          {SHOW_ADVANCED_METRICS && (
            <>
              <span
                className={`px-1.5 py-0.5 border ${
                  sf.targetedShare >= 80
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
              >
                {sf.targetedShare}% fields
              </span>
              <span
                className={`px-1.5 py-0.5 border ${
                  shallow ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-gray-50 text-gray-600 border-gray-200"
                }`}
              >
                depth {pd.median}/{sf.propertyCount}
              </span>
            </>
          )}
          <span className="px-1.5 py-0.5 border bg-gray-50 text-gray-600 border-gray-200">
            {sq.totalAssertions} assertions
          </span>
        </span>
      </button>

      {open && (
        <div className="p-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Status codes</div>
            <div className="text-[13px] text-gray-700 space-y-1">
              <div>
                Declared by contract:{" "}
                {(sc.declared || []).map((d) => <Code key={d}>{d}</Code>)}
              </div>
              <div>
                Never tested:{" "}
                {sc.neverTested?.length ? (
                  sc.neverTested.map((d) => <Code key={d}>{d}</Code>)
                ) : (
                  <span className="text-green-700">none</span>
                )}
              </div>
              {SHOW_ADVANCED_METRICS && (
                <div>
                  Tested but not declared:{" "}
                  {sc.testedButNotDeclared?.length ? (
                    sc.testedButNotDeclared.map((d) => <Code key={d}>{d}</Code>)
                  ) : (
                    <span className="text-gray-400">none</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {SHOW_ADVANCED_METRICS && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Schema fields</div>
            <div className="text-[13px] text-gray-700 space-y-1">
              <div>
                {sf.propertyCount} properties · <strong>{sf.targetedByATest} targeted</strong> by a test
                ({sf.targetedShare}%)
              </div>
              <div>
                <span className="text-amber-700">{sf.payloadOnlyCount} payload-only</span>{" "}
                <span className="text-gray-400">— sent, but no test is about them</span>
              </div>
              <div>
                {sf.absentCount === 0 ? (
                  <span className="text-green-700">0 absent — every field appears somewhere</span>
                ) : (
                  <span className="text-red-700">{sf.absentCount} never appear in any body</span>
                )}
              </div>
              {sf.requiredNotTargeted?.length > 0 && (
                <div className="text-red-700">
                  Required but untargeted: {sf.requiredNotTargeted.map((r) => <Code key={r}>{r}</Code>)}
                </div>
              )}
            </div>
          </div>

          )}

          {SHOW_ADVANCED_METRICS && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Payload depth</div>
            <div className="text-[13px] text-gray-700">
              min {pd.min} · <strong>median {pd.median}</strong> · max {pd.max}
              {shallow && (
                <div className="text-amber-700 mt-0.5">
                  Median of {pd.median} against {sf.propertyCount} properties — these tests check field
                  validation, not whether a realistic request works.
                </div>
              )}
            </div>
          </div>

          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Scripts</div>
            <div className="text-[13px] text-gray-700 space-y-1">
              <div>
                {sq.withScript}/{endpoint.totalTestCases} have one · {sq.totalAssertions} assertions
                (min {sq.assertionsMin} / med {sq.assertionsMedian} / max {sq.assertionsMax})
              </div>
              <div>
                {sq.statusDriftCount === 0 ? (
                  <span className="text-green-700">No script contradicts its own expected status</span>
                ) : (
                  <span className="text-red-700">
                    {sq.statusDriftCount} scripts assert a status their test does not declare
                  </span>
                )}
              </div>
              {Object.keys(sq.bannedApiUsage || {}).length > 0 && (
                <div className="text-red-700">
                  Unsupported API used: {Object.keys(sq.bannedApiUsage).join(", ")}
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Headers</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-gray-700">
              {Object.entries(hd.presentPercent || {}).map(([h, pct]) => (
                <span key={h}>
                  <Code>{h}</Code>{" "}
                  <span className={pct >= 90 ? "text-green-700" : "text-amber-700"}>{pct}%</span>
                </span>
              ))}
              {hd.testsWithoutAuth > 0 && (
                <span className="text-gray-500">{hd.testsWithoutAuth} without auth</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Finding = ({ finding }) => {
  const tone = SEVERITY[finding.severity] || SEVERITY.low;
  return (
    <div className="flex gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0">
      <span className={`w-1 flex-shrink-0 rounded-sm ${tone.bar}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-gray-900">{finding.title}</span>
          <span className={`text-[10px] px-1.5 py-0.5 border rounded font-semibold uppercase tracking-wide ${tone.chip}`}>
            {finding.severity}
          </span>
          <span className="text-[11px] text-gray-500 font-mono">{finding.category}</span>
          {finding.affectedCases > 0 && (
            <span className="text-[11px] text-gray-500 tabular-nums">~{finding.affectedCases} cases</span>
          )}
        </div>
        {finding.endpoint && (
          <div className="text-[11px] text-gray-500 font-mono mt-0.5">{finding.endpoint}</div>
        )}
        <p className="text-[13px] text-gray-700 mt-1 leading-relaxed">{finding.detail}</p>
        {finding.action && (
          <p className="text-[13px] text-gray-900 mt-1.5">
            <span className="text-gray-400">Do this: </span>
            {finding.action}
          </p>
        )}
      </div>
    </div>
  );
};

const CollectionReview = ({ collectionId, companyId }) => {
  const { data, isLoading, isError, error } = useTestCaseReview(collectionId, companyId);
  const runReview = useRunTestCaseReview(collectionId, companyId, {
    onSuccess: (result) => {
      const status = result?.aiReview?.status;
      if (status === "SUCCESS") toast.success("Review complete");
      else toast.error(result?.aiReview?.errorMessage || "The review could not be completed");
    },
    onError: (e) => toast.error(e?.response?.data?.message || "The review could not be completed"),
  });

  if (!collectionId) {
    return (
      <div className="p-6 text-sm text-gray-500 font-apple">
        Select a collection to review the test cases generated for it.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-gray-500 font-apple">
        <CircularProgress size={16} /> Building the review…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-6 text-sm text-red-700 font-apple">
        {error?.response?.data?.message || "Could not load the review."}
      </div>
    );
  }

  const m = data?.metrics || {};
  const t = m.totals || {};
  const cov = m.coverage || {};
  const ai = data?.aiReview;
  const review = ai?.review;
  const total = m.totalTestCases || 0;
  const typeIndex = Object.keys(m.byType || {});

  return (
    <div className="flex flex-col gap-4 font-apple">
      {/* Headline */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="min-w-0 max-w-full">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">Collection review</div>
          <Tooltip title={m.collectionName || ""} arrow>
            <h2 className="text-xl font-semibold text-gray-900 truncate">{m.collectionName}</h2>
          </Tooltip>
        </div>
        <span className="text-[13px] text-gray-500 mb-1">
          {total} test cases across {m.endpointCount} endpoint{m.endpointCount === 1 ? "" : "s"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {ai?.stale && (
            <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1">
              Test cases changed since this review
            </span>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={runReview.isPending || total === 0}
            onClick={() => runReview.mutate()}
            startIcon={
              runReview.isPending ? <CircularProgress size={14} />
                : review ? <ArrowPathIcon className="h-4 w-4" />
                : <SparklesIcon className="h-4 w-4" />
            }
            sx={{ textTransform: "none", fontSize: 13 }}
          >
            {runReview.isPending ? "Reviewing…" : review ? "Run review again" : "Run AI review"}
          </Button>
        </div>
      </div>

      {/* Headline counts */}
      <div className="flex gap-2 flex-wrap">
        <Tile label="Test cases" value={total} />
        <Tile label="Positive" value={t.positive ?? 0} sub="expect 2xx" tone="good" />
        <Tile label="Negative" value={t.negative ?? 0} sub={`${t.negativeShare ?? 0}% of suite`} tone="warn" />
        <Tile
          label="Declared codes"
          value={`${cov.declaredStatusCodesCovered ?? 0}/${cov.declaredStatusCodes ?? 0}`}
          sub="covered by a test"
          tone={cov.declaredStatusCodesCovered === cov.declaredStatusCodes ? "good" : "bad"}
        />
        {SHOW_ADVANCED_METRICS && (
          <>
            <Tile
              label="Fields targeted"
              value={`${cov.propertiesTargetedShare ?? 0}%`}
              sub={`${cov.propertiesTargeted ?? 0} of ${cov.schemaProperties ?? 0}`}
              tone={(cov.propertiesTargetedShare ?? 0) >= 80 ? "good" : "warn"}
            />
            <Tile
              label="Median depth"
              value={cov.medianPayloadDepth ?? 0}
              sub={`of ${cov.schemaProperties ?? 0} properties`}
              tone="warn"
            />
            <Tile
              label="Reviewed"
              value={`${m.reviewed?.percent ?? 0}%`}
              sub={`${m.reviewed?.pending ?? 0} pending`}
              tone={m.reviewed?.percent === 100 ? "good" : "bad"}
            />
          </>
        )}
        <Tile label="Assertions" value={(cov.totalAssertions ?? 0).toLocaleString()} sub={`${cov.testsWithScript ?? 0} scripts`} />
      </div>

      {/* Contract integrity - the checks that should read as clean */}
      <Section title="Contract integrity" hint="measured against each endpoint's resolved contract">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-[13px]">
          {[
            {
              ok: (cov.propertiesAbsent ?? 0) === 0,
              good: "Every schema field appears in at least one request",
              bad: `${cov.propertiesAbsent} schema fields never appear in any request`,
            },
            {
              ok: (cov.scriptStatusDrift ?? 0) === 0,
              good: "No script contradicts its own expected status",
              bad: `${cov.scriptStatusDrift} scripts assert a different status`,
            },
            {
              ok: (cov.testsWithoutScript ?? 0) === 0,
              good: "Every test has a validation script",
              bad: `${cov.testsWithoutScript} tests have no script`,
            },
            {
              ok: (cov.nearDuplicateGroups ?? 0) === 0,
              good: "No duplicate or reworded test cases",
              bad: `${cov.nearDuplicateGroups} groups of near-duplicate titles`,
            },
          ].map((check, i) => (
            <div key={i} className="flex items-start gap-2">
              {check.ok ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              )}
              <span className={check.ok ? "text-gray-700" : "text-amber-800"}>
                {check.ok ? check.good : check.bad}
              </span>
            </div>
          ))}
        </div>

        {SHOW_ADVANCED_METRICS
          && (cov.statusCodesTestedButNotDeclared?.length > 0
          || cov.propertiesPayloadOnly > 0
          || (cov.bannedApiUsage?.length ?? 0) > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-100 text-[13px] text-gray-700 space-y-1">
            {cov.propertiesPayloadOnly > 0 && (
              <div>
                <span className="text-amber-700 font-medium">{cov.propertiesPayloadOnly} fields are payload-only</span>
                <span className="text-gray-500"> — sent in requests, but no test is about them.</span>
              </div>
            )}
            {cov.statusCodesTestedButNotDeclared?.length > 0 && (
              <div>
                Tested but never declared:{" "}
                {cov.statusCodesTestedButNotDeclared.map((c) => <Code key={c}>{c}</Code>)}
                <span className="text-gray-500"> — these fail forever against a healthy API unless protocol-level.</span>
              </div>
            )}
            {cov.bannedApiUsage?.length > 0 && (
              <div className="text-red-700">
                Scripts use unsupported API: {cov.bannedApiUsage.join(", ")}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Type mix */}
      <Section title="Test types" hint="what the suite spends its cases on">
        <DistributionBar
          data={m.byType || {}}
          total={total}
          palette={(key) => TYPE_COLOURS[typeIndex.indexOf(key) % TYPE_COLOURS.length]}
        />
      </Section>

      {/* Per endpoint, expandable */}
      <Section title="By endpoint" hint="expand for contract coverage detail">
        <div className="flex flex-col gap-2">
          {(m.endpoints || []).map((e) => (
            <EndpointCoverage key={e.endpointId} endpoint={e} />
          ))}
        </div>
      </Section>

      {/* Computed observations */}
      {SHOW_ADVANCED_METRICS && m.observations?.length > 0 && (
        <Section title="What the numbers show" hint="computed, not inferred">
          <div className="flex flex-col gap-1.5">
            {m.observations.map((o, i) => (
              <div key={i} className="flex items-start gap-2 text-[13px] text-gray-700">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  <span className="text-gray-400 mr-1.5">{KIND_LABEL[o.kind] || o.kind}</span>
                  {o.message}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* AI assessment */}
      <Section
        title="AI review"
        hint={
          ai?.createdAt
            ? `last run ${new Date(ai.createdAt).toLocaleString()}`
              + (ai.durationMs ? ` · ${(ai.durationMs / 1000).toFixed(1)}s` : "")
            : "not run yet"
        }
      >
        {!ai && (
          <div className="text-[13px] text-gray-500">
            Nothing yet. Run the review for an assessment of what these numbers mean — untested
            documented rules, wrong expectations, and where the real risk sits.
          </div>
        )}

        {ai?.status === "FAILED" && (
          <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{ai.errorMessage || "The review failed."}</span>
          </div>
        )}

        {review && (
          <div className="flex flex-col gap-4">
            <div className="border-l-4 border-blue-500 bg-blue-50 pl-3 pr-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Verdict</span>
                {review.confidence && (
                  <span className="text-[10px] text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded">
                    {review.confidence} confidence
                  </span>
                )}
              </div>
              <p className="text-[13px] text-gray-800 leading-relaxed">{review.verdict}</p>
            </div>

            {review.strengths?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Holding up well</div>
                <div className="flex flex-col gap-1">
                  {review.strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-[13px] text-gray-700">
                      <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.findings?.length > 0 && (
              <div className="border border-gray-200">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                  {review.findings.length} findings
                </div>
                {[...review.findings]
                  .sort((a, b) => {
                    const rank = { high: 0, medium: 1, low: 2 };
                    return (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3);
                  })
                  .map((f, i) => <Finding key={i} finding={f} />)}
              </div>
            )}

            {review.untestedDocumentedRules?.length > 0 && (
              <div className="border border-gray-200">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <BookOpenIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                    Documented behaviour with no test ({review.untestedDocumentedRules.length})
                  </span>
                </div>
                {review.untestedDocumentedRules.map((u, i) => (
                  <div key={i} className="px-4 py-2.5 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Code>{u.field}</Code>
                      <span className="text-[11px] text-gray-500 font-mono">{u.endpoint}</span>
                    </div>
                    <p className="text-[13px] text-gray-800 mt-1">{u.rule}</p>
                    {u.whyItMatters && (
                      <p className="text-[12px] text-gray-500 mt-0.5">{u.whyItMatters}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {review.nextSteps?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Where to start</div>
                <ol className="flex flex-col gap-1.5">
                  {review.nextSteps.map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-[13px] text-gray-700">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold flex items-center justify-center tabular-nums">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
};

export default CollectionReview;
